import React, { useEffect } from 'react';
import styled from 'styled-components';
import { MembershipType, ContactPreference, Title, Gender, ProfessionalPath } from '../../types/member';
import FormField from '../common/FormField';
import DateOfBirthField from '../common/DateOfBirthField';
import BilingualDoubleInput from './BilingualDoubleInput';
import PhoneWithDialCode from './PhoneWithDialCode';
import { GREECE } from '../../data/countries';
import { FormRow } from '../../design-system/patterns/FormLayout';
import { Text } from '../../design-system/components/Typography';
import { spacing } from '../../design-system/tokens/spacing';
import { typography } from '../../design-system/tokens/typography';
import { borders } from '../../design-system/tokens/borders';

const LockedCountryField = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
  width: 100%;
`;

const LockedCountryLabel = styled.span`
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text};
  font-family: ${typography.fontFamily.primary};
`;

const LockedCountryValue = styled.div`
  padding: ${spacing[3]} ${spacing[4]};
  border: ${borders.width.base} solid ${props => props.theme.colors.border};
  border-radius: ${borders.radius.lg};
  font-size: ${typography.fontSize.sm};
  font-family: ${typography.fontFamily.primary};
  background: ${props => props.theme.colors.surfaceHover};
  color: ${props => props.theme.colors.text};
`;

const SectionHeading = styled.h3`
  color: ${props => props.theme.colors.text};
  margin-bottom: ${spacing[4]};
  margin-top: ${spacing[6]};
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.xl};
  font-weight: 600;
  &:first-of-type {
    margin-top: 0;
  }
`;

const CriteriaBlock = styled.div`
  padding: ${spacing[5]};
  background: ${props => props.theme.colors.icomLightBlue || props.theme.colors.surfaceHover};
  border-radius: ${props => props.theme.borders?.radius?.lg || '12px'};
  border-left: 3px solid ${props => props.theme.colors.primary};
  margin-top: ${spacing[6]};
  font-size: ${typography.fontSize.sm};
  line-height: 1.7;
  color: ${props => props.theme.colors.text};
`;

const CriteriaLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[3]};
  margin-top: ${spacing[4]};
`;

const CriteriaLink = styled.a`
  color: ${props => props.theme.colors.primary};
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const CriteriaParagraph = styled.p<{ $margin?: keyof typeof spacing; $fontSize?: string }>`
  margin-top: ${props => (props.$margin !== undefined ? spacing[props.$margin] : spacing[2])};
  font-size: ${props => props.$fontSize || 'inherit'};
`;

export interface PersonalInfoSectionProps {
  formData: {
    title?: string;
    gender?: string;
    membershipType?: MembershipType;
    dateOfBirth?: string;
    dateOfBirthDay?: string;
    dateOfBirthMonth?: string;
    dateOfBirthYear?: string;
    firstNameEn?: string;
    firstNameGr?: string;
    lastNameEn?: string;
    lastNameGr?: string;
    residentialAddressEn?: string;
    residentialAddressGr?: string;
    residentialCityEn?: string;
    residentialCityGr?: string;
    residentialPostalCode?: string;
    residentialCountryEn?: string;
    residentialCountryGr?: string;
    phone?: string;
    mobile?: string;
    contactPreference?: string;
    website?: string;
    professionalPath?: string;
    organisationNameEn?: string;
    organisationNameGr?: string;
  };
  setFormData: React.Dispatch<React.SetStateAction<any>>;
  t: (key: string) => string;
  showMembershipType?: boolean;
  showCriteriaBlock?: boolean;
  showTitleAndGender?: boolean;
  showOrganisationWebsitePath?: boolean;
  /** When true, country is fixed to Greece (individual registration). */
  lockCountryToGreece?: boolean;
  /** Admin records can also be honorary members. */
  includeHonoraryMembership?: boolean;
  fieldErrors?: Record<string, string>;
  onFieldBlur?: (fieldName: string) => void;
}

export const PersonalInfoSection: React.FC<PersonalInfoSectionProps> = ({
  formData,
  setFormData,
  t,
  showMembershipType = true,
  showCriteriaBlock = false,
  showTitleAndGender = false,
  showOrganisationWebsitePath = false,
  lockCountryToGreece = false,
  includeHonoraryMembership = false,
  fieldErrors = {},
  onFieldBlur,
}) => {
  useEffect(() => {
    if (!lockCountryToGreece) return;
    if (
      formData.residentialCountryEn === GREECE.nameEn &&
      formData.residentialCountryGr === GREECE.nameGr
    ) {
      return;
    }
    setFormData((prev: any) => ({
      ...prev,
      residentialCountryEn: GREECE.nameEn,
      residentialCountryGr: GREECE.nameGr,
    }));
  }, [
    lockCountryToGreece,
    formData.residentialCountryEn,
    formData.residentialCountryGr,
    setFormData,
  ]);

  return (
    <>
      {showTitleAndGender && (
        <FormRow>
          <FormField
            id="register-title"
            name="title"
            label={t('register.titleOption')}
            select
            options={[
              { value: '', label: t('profile.selectTitle') },
              { value: Title.Mr.toString(), label: t('register.titleMr') },
              { value: Title.Ms.toString(), label: t('register.titleMs') },
              { value: Title.Mrs.toString(), label: t('register.titleMrs') },
              { value: Title.Dr.toString(), label: t('register.titleDr') },
              { value: Title.Prof.toString(), label: t('register.titleProf') },
              { value: Title.Other.toString(), label: t('register.titleOther') },
            ]}
            value={formData.title ?? ''}
            onChange={(e) => setFormData((prev: any) => ({ ...prev, title: e.target.value }))}
          />
          <FormField
            id="register-gender"
            name="gender"
            label={t('profile.genderLabel')}
            select
            options={[
              { value: '', label: t('profile.selectGender') },
              { value: Gender.Female.toString(), label: t('profile.gender.female') },
              { value: Gender.Male.toString(), label: t('profile.gender.male') },
              { value: Gender.Other.toString(), label: t('profile.gender.other') },
              { value: Gender.PreferNotToSay.toString(), label: t('profile.gender.preferNotToSay') },
            ]}
            value={formData.gender ?? ''}
            onChange={(e) => setFormData((prev: any) => ({ ...prev, gender: e.target.value }))}
          />
        </FormRow>
      )}
      {showMembershipType && (
        <>
          <FormField
            id="register-membership-type"
            name="membershipType"
            label={t('register.membershipType')}
            select
            options={[
              { value: MembershipType.Regular.toString(), label: t('register.regular') },
              { value: MembershipType.Student.toString(), label: t('register.student') },
              { value: MembershipType.Retired.toString(), label: t('register.retiredProfessional') },
              ...(includeHonoraryMembership
                ? [{ value: MembershipType.Honorary.toString(), label: t('membership.honorary') }]
                : []),
            ]}
            value={String(formData.membershipType ?? MembershipType.Regular)}
            onChange={(e) => {
              const membershipType = parseInt(e.target.value, 10) as MembershipType;
              setFormData((prev: any) => ({
                ...prev,
                membershipType,
                isHonoraryMember: membershipType === MembershipType.Honorary,
              }));
            }}
          />
          {formData.membershipType === MembershipType.Student && (
            <Text size="sm" color="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
              {t('register.individual.studentEligibilityNote')}
            </Text>
          )}
        </>
      )}
      <FormRow>
        <DateOfBirthField
          id="register-date-of-birth"
          value={formData.dateOfBirth ?? ''}
          parts={{
            day: formData.dateOfBirthDay ?? '',
            month: formData.dateOfBirthMonth ?? '',
            year: formData.dateOfBirthYear ?? '',
          }}
          onPartsChange={(parts) =>
            setFormData((prev: any) => ({
              ...prev,
              dateOfBirthDay: parts.day,
              dateOfBirthMonth: parts.month,
              dateOfBirthYear: parts.year,
            }))
          }
          onChange={(dateOfBirth) => setFormData((prev: any) => ({ ...prev, dateOfBirth }))}
          error={fieldErrors.dateOfBirth}
        />
      </FormRow>
      <FormRow>
        <BilingualDoubleInput
          idPrefix="register-last-name"
          label={t('common.lastName')}
          valueEn={formData.lastNameEn ?? ''}
          valueGr={formData.lastNameGr ?? ''}
          onChangeEn={(v) => setFormData((prev: any) => ({ ...prev, lastNameEn: v }))}
          onChangeGr={(v) => setFormData((prev: any) => ({ ...prev, lastNameGr: v }))}
          required={showMembershipType}
          error={fieldErrors.lastName}
          greekFirst
        />
        <BilingualDoubleInput
          idPrefix="register-first-name"
          label={t('register.firstNameLabel')}
          valueEn={formData.firstNameEn ?? ''}
          valueGr={formData.firstNameGr ?? ''}
          onChangeEn={(v) => setFormData((prev: any) => ({ ...prev, firstNameEn: v }))}
          onChangeGr={(v) => setFormData((prev: any) => ({ ...prev, firstNameGr: v }))}
          required={showMembershipType}
          error={fieldErrors.firstName}
          greekFirst
        />
      </FormRow>
      <SectionHeading>{t('register.individual.yourContactInfo')}</SectionHeading>
      <BilingualDoubleInput
        label={t('register.address')}
        valueEn={formData.residentialAddressEn ?? ''}
        valueGr={formData.residentialAddressGr ?? ''}
        onChangeEn={(v) => setFormData((prev: any) => ({ ...prev, residentialAddressEn: v }))}
        onChangeGr={(v) => setFormData((prev: any) => ({ ...prev, residentialAddressGr: v }))}
        greekFirst
      />
      <FormRow>
        <BilingualDoubleInput
          label={t('register.personalCity')}
          valueEn={formData.residentialCityEn ?? ''}
          valueGr={formData.residentialCityGr ?? ''}
          onChangeEn={(v) => setFormData((prev: any) => ({ ...prev, residentialCityEn: v }))}
          onChangeGr={(v) => setFormData((prev: any) => ({ ...prev, residentialCityGr: v }))}
          greekFirst
        />
        <FormField
          label={t('register.personalPostalCode')}
          value={formData.residentialPostalCode ?? ''}
          error={fieldErrors.residentialPostalCode}
          alignWithBilingual
          onChange={(e) =>
            setFormData((prev: any) => ({ ...prev, residentialPostalCode: e.target.value }))
          }
          onBlur={() => onFieldBlur?.('residentialPostalCode')}
        />
      </FormRow>
      {lockCountryToGreece ? (
        <LockedCountryField>
          <LockedCountryLabel id="register-country-label">
            {t('register.personalCountry')}
          </LockedCountryLabel>
          <LockedCountryValue
            role="text"
            aria-labelledby="register-country-label"
          >
            {GREECE.nameGr} / {GREECE.nameEn}
          </LockedCountryValue>
        </LockedCountryField>
      ) : (
        <BilingualDoubleInput
          label={t('register.personalCountry')}
          valueEn={formData.residentialCountryEn ?? ''}
          valueGr={formData.residentialCountryGr ?? ''}
          onChangeEn={(v) => setFormData((prev: any) => ({ ...prev, residentialCountryEn: v }))}
          onChangeGr={(v) => setFormData((prev: any) => ({ ...prev, residentialCountryGr: v }))}
          greekFirst
        />
      )}
      <FormRow>
        <PhoneWithDialCode
          id="register-phone"
          name="phone"
          label={t('register.individual.telephoneWithCountryCode')}
          autoComplete="tel"
          value={formData.phone ?? ''}
          error={fieldErrors.phone}
          onChange={(phone) => setFormData((prev: any) => ({ ...prev, phone }))}
          onBlur={() => onFieldBlur?.('phone')}
        />
        <PhoneWithDialCode
          id="register-mobile"
          name="mobile"
          label={t('register.individual.mobileWithCountryCode')}
          autoComplete="tel-national"
          value={formData.mobile ?? ''}
          error={fieldErrors.mobile}
          onChange={(mobile) => setFormData((prev: any) => ({ ...prev, mobile }))}
          onBlur={() => onFieldBlur?.('mobile')}
        />
      </FormRow>
      <FormField
        id="register-contact-preference"
        name="contactPreference"
        label={t('register.contactPreference')}
        select
        options={[
          { value: '', label: t('register.individual.useMyContactInfo') },
          { value: ContactPreference.Personal.toString(), label: t('register.contactPreferencePersonal') },
          { value: ContactPreference.Professional.toString(), label: t('register.contactPreferenceProfessional') },
        ]}
        value={formData.contactPreference ?? ''}
        onChange={(e) =>
          setFormData((prev: any) => ({ ...prev, contactPreference: e.target.value }))
        }
      />
      {showOrganisationWebsitePath && (
        <>
          <FormField
            label={t('register.professionalPath')}
            select
            options={[
              { value: '', label: t('profile.selectProfessionalPath') },
              { value: ProfessionalPath.ActiveMuseumProfessional.toString(), label: t('register.professionalPathActive') },
              { value: ProfessionalPath.Student.toString(), label: t('register.professionalPathStudent') },
              { value: ProfessionalPath.InterestedNonProfessional.toString(), label: t('register.professionalPathInterested') },
            ]}
            value={formData.professionalPath ?? ''}
            onChange={(e) =>
              setFormData((prev: any) => ({ ...prev, professionalPath: e.target.value }))
            }
          />
          <BilingualDoubleInput
            label={t('profile.organisationName')}
            valueEn={formData.organisationNameEn ?? ''}
            valueGr={formData.organisationNameGr ?? ''}
            onChangeEn={(v) =>
              setFormData((prev: any) => ({ ...prev, organisationNameEn: v }))
            }
            onChangeGr={(v) =>
              setFormData((prev: any) => ({ ...prev, organisationNameGr: v }))
            }
            shortLabels
          />
        </>
      )}
      {showCriteriaBlock && (
        <CriteriaBlock>
          <strong>{t('register.individual.icomIntro')}</strong>
          <CriteriaParagraph $margin={3}>{t('register.individual.criteriaHeading')}</CriteriaParagraph>
          <CriteriaParagraph>{t('register.individual.criteriaMuseumProfessionals')}</CriteriaParagraph>
          <CriteriaParagraph>{t('register.individual.criteriaBullets')}</CriteriaParagraph>
          <CriteriaLinks>
            <CriteriaLink
              href="https://icom.museum/wp-content/uploads/2018/07/2017_ICOM_Statutes_EN.pdf"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('register.individual.criteriaLinkStatutes')}
            </CriteriaLink>
            <CriteriaLink href="https://icom.museum/en/privacy-policy/" target="_blank" rel="noopener noreferrer">
              {t('register.individual.criteriaLinkPrivacy')}
            </CriteriaLink>
            <CriteriaLink
              href="https://icom.museum/en/network/committees-directory/?type=136"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('register.individual.criteriaLinkNationalCommittees')}
            </CriteriaLink>
            <CriteriaLink href="https://icom.museum" target="_blank" rel="noopener noreferrer">
              {t('register.individual.criteriaLinkWebsite')}
            </CriteriaLink>
          </CriteriaLinks>
          <CriteriaParagraph $margin={4} $fontSize={typography.fontSize.xs}>
            {t('register.individual.sendToNationalCommittee')}
          </CriteriaParagraph>
        </CriteriaBlock>
      )}
    </>
  );
};

export default PersonalInfoSection;
